import { useEffect, useState } from "react";
import { supabase } from "@/shared/integrations/supabase/client";
import type { AssetPhoto, PhotoWithUrl } from "./types";

export function usePhotoSignedUrls(propPhotos: AssetPhoto[]) {
  const [photosWithUrls, setPhotosWithUrls] = useState<PhotoWithUrl[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const generateSignedUrls = async () => {
      setLoading(true);
      const urlPromises = propPhotos.map(async (photo) => {
        if (!photo.file_path) return { ...photo, signedUrl: undefined };

        // Check if it's a Firebase URL (or any external URL)
        if (photo.file_path.startsWith('http://') || photo.file_path.startsWith('https://')) {
          return {
            ...photo,
            signedUrl: photo.file_path,
          };
        }

        // For Supabase storage paths, generate signed URLs
        try {
          const { data } = await supabase.storage
            .from('asset-documents')
            .createSignedUrl(photo.file_path, 3600); // 1 hour expiry

          return {
            ...photo,
            signedUrl: data?.signedUrl || undefined,
          };
        } catch (err) {
          console.warn(`Failed to generate URL for photo ${photo.id}:`, err);
          return { ...photo, signedUrl: undefined };
        }
      });

      const results = await Promise.all(urlPromises);
      setPhotosWithUrls(results);
      setLoading(false);
    };

    if (propPhotos.length > 0) {
      generateSignedUrls();
    } else {
      setPhotosWithUrls([]);
      setLoading(false);
    }
  }, [propPhotos]);

  return { photosWithUrls, loading };
}
